import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import * as React from 'react';

import { Badge } from '~/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import { Skeleton } from '~/components/ui/skeleton';
import { api } from '~/lib/api';
import { requireOwnerOrAdmin } from '~/lib/route-auth';

export const Route = createFileRoute('/rooms/$roomId/permissions')({
  beforeLoad: async () => {
    await requireOwnerOrAdmin();
  },
  component: PermissionMatrixRoute
});

export function PermissionMatrixComponent({ roomId }: { roomId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['permissions', roomId],
    queryFn: () =>
      api.get<{
        permissions: Array<{
          id: string;
          resourceType: string;
          resourceId: string;
          subjectType: string;
          subjectId: string;
          action: string;
        }>;
      }>(`/api/permissions?resourceId=${encodeURIComponent(roomId)}`)
  });

  const permissions = data?.permissions ?? [];

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          <h2 className="text-2xl font-bold">Permissions</h2>
        </div>
        <p className="text-sm text-muted-foreground">Room: {roomId}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Access Matrix</CardTitle>
          <CardDescription>Who can access what in this room</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : permissions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Lock className="h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">
                No explicit permissions set
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {permissions.map((perm) => (
                <div
                  key={perm.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div>
                    <p className="font-medium">{perm.subjectId}</p>
                    <p className="text-sm text-muted-foreground">
                      {perm.subjectType} — {perm.action}
                    </p>
                  </div>
                  <Badge variant="outline">{perm.action}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PermissionMatrixRoute() {
  const { roomId } = Route.useParams();
  return <PermissionMatrixComponent roomId={roomId} />;
}
